// The in-memory API's agency console (M5-05; contracts/api/agency.ts): approvals SLA, supervisor
// load, cost-to-serve and break-glass, with the API's rules:
// - Operator reads: a session with `me.agency` (else 404), fanned out over the grants of level
//   `operator` only; another company's rows never appear.
// - Approvals SLA (target default 240 min): `pending` = pending now, `pending_over_sla` = pending
//   older than the target, `decided` = approved | edited | rejected with `decided_at` in the week,
//   `decided_within_sla` = decided in ≤ target minutes, `expired` = expired in the week, median and
//   p90 of the week's decision minutes (null when none), `overdue` = pending past the target,
//   oldest first, at most 100.
// - Supervisor load: one row per person (user id, else `membership:{id}`); a DE's supervisor is
//   the active holder of its seat's supervisor seat; unsupervised active DEs say why
//   (`no_supervisor_seat` | `supervisor_seat_unfilled`).
// - Cost-to-serve per month: charged = client-visible `de_subscription` + `de_usage`; model =
//   `agent_*` + `estimated` (any visibility); supervision = `human_time` on the DE's seat; other =
//   the remaining agency-visibility rows; cost = model + supervision + other; margin = charged −
//   cost; clients sum per company, templates per (template_key, currency), never across currencies.
// - Break-glass: a request names an operator-granted company (404), a reason of 10..2000 chars,
//   5..240 minutes and a scope id for space/channel only (422); one live (requested | active)
//   session per requester and company (409). A second agency ADMIN (not the requester) or the
//   company's OWNER approves or rejects a request (409 once decided or older than 24 h); the
//   requester or an agency admin ends it from the console, a company owner or admin from the
//   company. An active session past `expires_at` reads `expired` (the server's job).
//
// The fake has no `human_time` ledger rows: the approved supervision and founder time of a DE's
// seat stands in for them at fixed hourly rates (what the time → cost booking would post).
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { shiftWeek } from "../focus/weeks";
import { type Handler, json, notFound, page, problem, validation } from "./fake";
import {
  COMPANY_IDS,
  costLedger,
  type FakeCompany,
  type FakeDe,
  timeEntries,
  uid,
  weekOf,
} from "./fake-data";
import type { CostEvent, MeResponse } from "./types";

export type BreakGlassSession = OperationOutput<"breakGlass.list">["data"][number];
// the stored row (Parity P6a: the list adds the caller's `can_view` / `can_decide` on top)
type Approval = OperationOutput<"my.approvals">["data"][number];
type ApprovalsSla = OperationOutput<"operator.approvalsSla.get">;
type SupervisorLoad = OperationOutput<"operator.supervisorLoad.get">;
type CostToServe = OperationOutput<"operator.costToServe.get">;
type DeRow = OperationOutput<"operator.digitalEmployees.list">["data"][number];

/** HTVA, the agency of the demo operator (`/v1/me` `agency.agency_id`). */
export const FAKE_AGENCY_ID = "0193a0a0-0000-7000-8000-00000000a9e1";
/** Agency staff who are not demo sign-ins: the requesters and deciders of the seeded sessions. */
export const AGENCY_STAFF = {
  theo: { id: uid(0xa9e0, 1), name: "Theo Admin" },
  nina: { id: uid(0xa9e0, 2), name: "Nina Ng" },
} as const;

export const SLA_DEFAULT_MINUTES = 240;
const REQUEST_TTL_MS = 24 * 3_600_000;
const SUPERVISION_RATE_MINOR = 4_500; // per hour
const FOUNDER_RATE_MINOR = 9_000; // per hour
const MIN = 60_000;

const WEEK = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SCOPES = ["company", "space", "channel"] as const;

export type BreakGlassRead = OperationOutput<"breakGlass.reads">["data"][number];

export type AgencyState = {
  sessions: BreakGlassSession[];
  /** BREAK-GLASS: the companies' read logs (the API writes them on reads under a session). */
  reads: BreakGlassRead[];
  /** Supervisor seats nobody holds now (the person left): their DEs are unsupervised. */
  vacantSeats: Set<string>;
};

export function createAgencyState(now: () => Date): AgencyState {
  const companyIds = COMPANY_IDS;
  const t = now().getTime();
  const at = (minutesAgo: number) => new Date(t - minutesAgo * MIN).toISOString();
  const session = (
    n: number,
    patch: Partial<BreakGlassSession> &
      Pick<BreakGlassSession, "company_id" | "reason" | "state" | "requested_at">,
  ): BreakGlassSession => ({
    id: uid(0xb6a0, n),
    agency_id: FAKE_AGENCY_ID,
    requested_by_user_id: AGENCY_STAFF.theo.id,
    requested_by_name: AGENCY_STAFF.theo.name,
    scope: "company",
    scope_id: null,
    duration_minutes: 60,
    decided_by_kind: null,
    decided_by_user_id: null,
    decided_by_name: null,
    decided_at: null,
    reject_reason: null,
    starts_at: null,
    expires_at: null,
    ended_at: null,
    ended_by_user_id: null,
    ended_by_name: null,
    end_reason: null,
    created_at: patch.requested_at,
    updated_at: patch.requested_at,
    ...patch,
  });
  const acme = companyIds.acme;
  const state: AgencyState = {
    sessions: [
      // Live sessions sit on HTVA and Gamma, so the demo owner's Acme and Beta screens show no
      // banner until a test asks for one. Live now on HTVA: approved by a second agency admin
      // 45 minutes ago, 2 hours long.
      session(1, {
        company_id: companyIds.htva,
        reason: "The gate numbers import stopped; checking the import mapping with the founder.",
        state: "active",
        duration_minutes: 120,
        requested_at: at(50),
        decided_by_kind: "agency_admin",
        decided_by_user_id: AGENCY_STAFF.nina.id,
        decided_by_name: AGENCY_STAFF.nina.name,
        decided_at: at(45),
        starts_at: at(45),
        expires_at: at(45 - 120),
        updated_at: at(45),
      }),
      // Waiting on Gamma: a space-scoped request.
      session(2, {
        company_id: companyIds.gamma,
        requested_by_user_id: AGENCY_STAFF.nina.id,
        requested_by_name: AGENCY_STAFF.nina.name,
        reason: "Gus's screening board lost its custom fields; I need to inspect the space.",
        state: "requested",
        scope: "space",
        scope_id: uid(0xb6b0, 1),
        duration_minutes: 30,
        requested_at: at(25),
      }),
      // History: an ended session on Acme and a refused one on Beta.
      session(3, {
        company_id: acme,
        reason: "The Friday report failed to send; verifying the recipients list.",
        state: "ended",
        duration_minutes: 60,
        requested_at: at(3 * 24 * 60),
        decided_by_kind: "company_owner",
        decided_by_user_id: "0193a0a0-0000-7000-8000-0000000000b2",
        decided_by_name: "Maya Owner",
        decided_at: at(3 * 24 * 60 - 5),
        starts_at: at(3 * 24 * 60 - 5),
        expires_at: at(3 * 24 * 60 - 65),
        ended_at: at(3 * 24 * 60 - 30),
        ended_by_user_id: AGENCY_STAFF.theo.id,
        ended_by_name: AGENCY_STAFF.theo.name,
        end_reason: "Fixed: one recipient address had a typo.",
        updated_at: at(3 * 24 * 60 - 30),
      }),
      session(4, {
        company_id: companyIds.beta,
        reason: "Bea's outreach stalled; want to look at the last week of runs.",
        state: "rejected",
        duration_minutes: 240,
        requested_at: at(6 * 24 * 60),
        decided_by_kind: "agency_admin",
        decided_by_user_id: AGENCY_STAFF.nina.id,
        decided_by_name: AGENCY_STAFF.nina.name,
        decided_at: at(6 * 24 * 60 - 20),
        reject_reason: "Ask Beta for an export first; no access needed.",
        ended_at: at(6 * 24 * 60 - 20),
        updated_at: at(6 * 24 * 60 - 20),
      }),
    ],
    // BREAK-GLASS: what Theo read on Acme during the ended session (the owner's log).
    reads: [
      ["channel", 20, "GET /v1/companies/:co/channels/:id"],
      ["topic", 18, "GET /v1/companies/:co/topics/:id"],
      ["thread", 17, "GET /v1/companies/:co/threads/:id/messages"],
    ].map(([kind, minutesIn, route], i) => {
      const read = at(3 * 24 * 60 - 5 - Number(minutesIn));
      return {
        company_id: acme,
        id: uid(0xb6c0, i + 1),
        session_id: uid(0xb6a0, 3),
        agency_user_id: AGENCY_STAFF.theo.id,
        agency_user_name: AGENCY_STAFF.theo.name,
        subject_kind: kind as BreakGlassRead["subject_kind"],
        subject_id: uid(0xb6d0, i + 1),
        route: String(route),
        minute: new Date(Math.floor(new Date(read).getTime() / MIN) * MIN).toISOString(),
        read_at: read,
      };
    }),
    // Beta's office manager left: Bea's supervisor seat is unfilled.
    vacantSeats: new Set([uid(0x2000, 0xb11)]),
  };
  return state;
}

export type AgencyDeps = {
  now: () => Date;
  todayWeek: () => string;
  companies: () => FakeCompany[];
  desOf: (companyId: string) => FakeDe[];
  /** The Agents twin's approvals of a company (every state). */
  approvalsOf: (companyId: string) => readonly Approval[];
  /** Cost rows posted through the API (Setup › Costs) on top of the generated ledger. */
  postedCosts: (companyId: string) => readonly CostEvent[];
};

type Ref = { id: string; slug: string; name: string };
type LedgerItem = Pick<
  CostEvent,
  "kind" | "visibility" | "amount_minor" | "currency" | "occurred_at" | "seat_id"
>;

const minutesBetween = (from: string, to: string) =>
  Math.max(0, Math.floor((new Date(to).getTime() - new Date(from).getTime()) / MIN));

/** `percentile_cont`: linear interpolation between the closest ranks. */
function percentile(sorted: number[], p: number): number | null {
  if (!sorted.length) return null;
  const rank = (sorted.length - 1) * p;
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  const a = sorted[lo] ?? 0;
  const b = sorted[hi] ?? 0;
  return Math.round((a + (b - a) * (rank - lo)) * 10) / 10;
}

/** The agency console's facts about one DE, shared with the operator DE list (fake-ops). */
export type AgencyFacts = {
  supervisorOf(de: FakeDe): { seat_id: string; title: string; name: string | null } | null;
  deWeek(
    de: FakeDe,
    week: string,
  ): Pick<
    DeRow,
    | "week_key"
    | "supervisor"
    | "cost_minor"
    | "currency"
    | "supervision_minutes"
    | "pending_approvals"
  >;
};

export function createAgency(
  state: AgencyState,
  deps: AgencyDeps,
): { handlers: Partial<Record<RouteId, Handler>>; facts: AgencyFacts } {
  const nowIso = () => deps.now().toISOString();
  const companyOf = (id: string) => deps.companies().find((c) => c.id === id) ?? null;
  const refOf = (c: FakeCompany): Ref => ({ id: c.id, slug: c.slug, name: c.name });

  /** The companies the session's agency user operates (grants of level `operator`). */
  const operated = (me: MeResponse): FakeCompany[] =>
    (me.agency?.grants ?? [])
      .filter((g) => g.level === "operator")
      .map((g) => companyOf(g.company_id))
      .filter((c): c is FakeCompany => c !== null);

  // Supervision -------------------------------------------------------------------------------

  /** The active holder of the DE's supervisor seat, or why there is none. */
  function supervision(
    de: FakeDe,
  ):
    | { holder: { membership_id: string; name: string }; seat: { id: string; title: string } }
    | { holder: null; seat: { id: string; title: string } | null } {
    const sup = de.supervisor as FakeDe["supervisor"] | undefined;
    if (!sup?.seat_id) return { holder: null, seat: null };
    const seat = { id: sup.seat_id, title: sup.title };
    if (state.vacantSeats.has(sup.seat_id)) return { holder: null, seat };
    return { holder: { membership_id: sup.membership_id, name: sup.name }, seat };
  }

  /** Approved, live supervision minutes on the DE's seat in the week (the supervisor's). */
  function supervisionMinutes(de: FakeDe, week: string): number {
    return timeEntries(de, week, deps.todayWeek())
      .filter(
        (t) => t.kind === "supervision" && t.approval_status === "approved" && t.voided_at === null,
      )
      .reduce((s, t) => s + t.minutes, 0);
  }

  // Ledger ------------------------------------------------------------------------------------

  /** Weeks from the DE's start to today. */
  function weeksOf(de: FakeDe): string[] {
    const today = deps.todayWeek();
    return Array.from({ length: de.started_weeks_ago + 1 }, (_, i) => shiftWeek(today, -i));
  }

  /** Every ledger row on the DE's seat: generated, posted, and the human time stand-in. */
  function ledgerOf(de: FakeDe): LedgerItem[] {
    const company = companyOf(companyIdOf(de));
    const currency = company?.reporting_currency ?? "USD";
    const human: LedgerItem[] = weeksOf(de).flatMap((week) =>
      timeEntries(de, week, deps.todayWeek())
        .filter(
          (t) =>
            (t.kind === "supervision" || t.kind === "founder") &&
            t.approval_status === "approved" &&
            t.voided_at === null,
        )
        .map((t) => ({
          kind: "human_time" as const,
          visibility: "agency" as const,
          amount_minor: Math.round(
            (t.minutes / 60) * (t.kind === "founder" ? FOUNDER_RATE_MINOR : SUPERVISION_RATE_MINOR),
          ),
          currency,
          occurred_at: `${t.work_date}T17:00:00.000Z`,
          seat_id: t.seat_id,
        })),
    );
    return [
      ...costLedger(de, deps.todayWeek()),
      ...deps.postedCosts(companyIdOf(de)).filter((c) => c.seat_id === de.seat_id),
      ...human,
    ];
  }

  /** Minutes of approved supervision and founder time behind the month's human_time rows. */
  function humanMinutesInMonth(de: FakeDe, month: string): number {
    return weeksOf(de)
      .flatMap((week) => timeEntries(de, week, deps.todayWeek()))
      .filter(
        (t) =>
          (t.kind === "supervision" || t.kind === "founder") &&
          t.approval_status === "approved" &&
          t.voided_at === null &&
          t.work_date.startsWith(month),
      )
      .reduce((s, t) => s + t.minutes, 0);
  }

  const companyIdOf = (de: FakeDe): string => COMPANY_IDS[de.company];

  // Facts for the operator DE list ------------------------------------------------------------

  const facts: AgencyFacts = {
    supervisorOf(de) {
      const s = supervision(de);
      if (!s.seat) return null;
      return { seat_id: s.seat.id, title: s.seat.title, name: s.holder?.name ?? null };
    },
    deWeek(de, week) {
      const companyId = companyIdOf(de);
      const currency = companyOf(companyId)?.reporting_currency ?? "USD";
      const cost = ledgerOf(de)
        .filter((r) => r.currency === currency && weekOf(r.occurred_at) === week)
        .reduce((s, r) => s + r.amount_minor, 0);
      return {
        week_key: week,
        supervisor: facts.supervisorOf(de),
        cost_minor: cost,
        currency,
        supervision_minutes: supervisionMinutes(de, week),
        pending_approvals: deps
          .approvalsOf(companyId)
          .filter((a) => a.state === "pending" && a.requested_by_membership_id === de.membership_id)
          .length,
      };
    },
  };

  // Approvals SLA -----------------------------------------------------------------------------

  type SlaStats = ApprovalsSla["totals"];

  function slaStats(list: readonly Approval[], week: string, sla: number): SlaStats {
    const now = nowIso();
    const pending = list.filter((a) => a.state === "pending");
    const ages = pending.map((a) => minutesBetween(a.created_at, now));
    const decided = list.filter(
      (a) =>
        (a.state === "approved" || a.state === "edited" || a.state === "rejected") &&
        a.decided_at !== null &&
        weekOf(a.decided_at) === week,
    );
    const took = decided
      .map(
        (a) =>
          (new Date(a.decided_at ?? a.created_at).getTime() - new Date(a.created_at).getTime()) /
          MIN,
      )
      .sort((x, y) => x - y);
    return {
      pending: pending.length,
      pending_over_sla: ages.filter((m) => m > sla).length,
      decided: decided.length,
      decided_within_sla: took.filter((m) => m <= sla).length,
      expired: list.filter(
        (a) => a.state === "expired" && weekOf(a.expires_at ?? a.updated_at) === week,
      ).length,
      median_minutes: percentile(took, 0.5),
      p90_minutes: percentile(took, 0.9),
      oldest_pending_minutes: ages.length ? Math.max(...ages) : null,
    };
  }

  function approvalsSla(me: MeResponse, url: URL): Response {
    const week = url.searchParams.get("week");
    if (week !== null && !WEEK.test(week)) return validation("week must be a week key");
    const rawSla = url.searchParams.get("sla_minutes");
    const sla = rawSla === null ? SLA_DEFAULT_MINUTES : Number(rawSla);
    if (!Number.isInteger(sla) || sla < 15 || sla > 7 * 24 * 60)
      return validation("sla_minutes must be a whole number from 15 to 10080");
    const now = nowIso();
    const everything: Approval[] = [];
    const companies: ApprovalsSla["companies"] = [];
    const approvers: ApprovalsSla["approvers"] = [];
    const overdue: ApprovalsSla["overdue"] = [];
    for (const c of operated(me)) {
      const wk = week ?? deps.todayWeek();
      const list = deps.approvalsOf(c.id);
      if (!list.length) continue;
      everything.push(...list);
      companies.push({ company: refOf(c), week_key: wk, ...slaStats(list, wk, sla) });
      const des = deps.desOf(c.id);
      const seats = [...new Set(list.map((a) => a.approver_seat_id))];
      for (const seatId of seats) {
        const de = des.find((d) => d.supervisor.seat_id === seatId);
        const s = de ? supervision(de) : null;
        approvers.push({
          company: refOf(c),
          seat: { id: seatId, title: de?.supervisor.title ?? "Approver seat" },
          holders: s?.holder ? [s.holder.name] : [],
          ...slaStats(
            list.filter((a) => a.approver_seat_id === seatId),
            wk,
            sla,
          ),
        });
      }
      for (const a of list) {
        const age = minutesBetween(a.created_at, now);
        if (a.state !== "pending" || age <= sla) continue;
        const de = des.find((d) => d.supervisor.seat_id === a.approver_seat_id);
        overdue.push({
          company: refOf(c),
          approval_id: a.id,
          kind: a.kind,
          summary: a.proposal.summary ?? a.proposal.title ?? null,
          requested_at: a.created_at,
          age_minutes: age,
          approver_seat: {
            id: a.approver_seat_id,
            title: de?.supervisor.title ?? "Approver seat",
          },
        });
      }
    }
    overdue.sort((x, y) => x.requested_at.localeCompare(y.requested_at));
    const totalsWeek = week ?? deps.todayWeek();
    const body: ApprovalsSla = {
      sla_minutes: sla,
      complete: true,
      incomplete_reasons: [],
      totals: slaStats(everything, totalsWeek, sla),
      companies,
      approvers,
      overdue: overdue.slice(0, 100),
    };
    return json(body);
  }

  // Supervisor load ---------------------------------------------------------------------------

  function supervisorLoad(me: MeResponse, url: URL): Response {
    const week = url.searchParams.get("week");
    if (week !== null && !WEEK.test(week)) return validation("week must be a week key");
    const now = nowIso();
    const people = new Map<string, SupervisorLoad["people"][number]>();
    const unsupervised: SupervisorLoad["unsupervised"] = [];
    for (const c of operated(me)) {
      const wk = week ?? deps.todayWeek();
      const approvals = deps.approvalsOf(c.id);
      const byHolder = new Map<
        string,
        { name: string; seats: Map<string, string>; des: FakeDe[] }
      >();
      for (const de of deps.desOf(c.id)) {
        if (de.status === "retired") continue;
        const s = supervision(de);
        if (!s.holder) {
          if (de.status === "active")
            unsupervised.push({
              company: refOf(c),
              de: { membership_id: de.membership_id, display_name: de.display_name },
              reason: s.seat ? "supervisor_seat_unfilled" : "no_supervisor_seat",
            });
          continue;
        }
        const entry = byHolder.get(s.holder.membership_id) ?? {
          name: s.holder.name,
          seats: new Map<string, string>(),
          des: [],
        };
        entry.seats.set(s.seat.id, s.seat.title);
        entry.des.push(de);
        byHolder.set(s.holder.membership_id, entry);
      }
      for (const [membershipId, h] of byHolder) {
        const seatIds = new Set(h.seats.keys());
        const waiting = approvals.filter(
          (a) => a.state === "pending" && seatIds.has(a.approver_seat_id),
        );
        const ages = waiting.map((a) => minutesBetween(a.created_at, now));
        const stats = {
          de_count: h.des.length,
          supervision_minutes: h.des.reduce((s, de) => s + supervisionMinutes(de, wk), 0),
          pending_approvals: waiting.length,
          oldest_pending_minutes: ages.length ? Math.max(...ages) : null,
          open_reviews: 0,
        };
        // The fake's supervisors are people without a sign-in: keyed by their membership.
        const key = `membership:${membershipId}`;
        const person = people.get(key) ?? {
          key,
          user_id: null,
          display_name: h.name,
          de_count: 0,
          supervision_minutes: 0,
          pending_approvals: 0,
          oldest_pending_minutes: null,
          open_reviews: 0,
          companies: [],
        };
        person.companies.push({
          company: refOf(c),
          week_key: wk,
          membership_id: membershipId,
          seats: [...h.seats].map(([id, title]) => ({ id, title })),
          des: h.des.map((d) => ({ membership_id: d.membership_id, display_name: d.display_name })),
          ...stats,
        });
        person.de_count += stats.de_count;
        person.supervision_minutes += stats.supervision_minutes;
        person.pending_approvals += stats.pending_approvals;
        person.open_reviews += stats.open_reviews;
        if (stats.oldest_pending_minutes !== null)
          person.oldest_pending_minutes = Math.max(
            person.oldest_pending_minutes ?? 0,
            stats.oldest_pending_minutes,
          );
        people.set(key, person);
      }
    }
    const body: SupervisorLoad = {
      complete: true,
      incomplete_reasons: [],
      people: [...people.values()].sort(
        (a, b) => b.de_count - a.de_count || a.display_name.localeCompare(b.display_name),
      ),
      unsupervised,
    };
    return json(body);
  }

  // Cost-to-serve -----------------------------------------------------------------------------

  type Money = Pick<
    CostToServe["des"][number],
    | "currency"
    | "charged_minor"
    | "model_minor"
    | "supervision_minor"
    | "other_minor"
    | "cost_minor"
    | "margin_minor"
  >;

  function split(rows: LedgerItem[], currency: string): Money {
    let charged = 0;
    let model = 0;
    let supervision = 0;
    let other = 0;
    for (const r of rows) {
      if (r.visibility === "client" && (r.kind === "de_subscription" || r.kind === "de_usage"))
        charged += r.amount_minor;
      else if (r.kind.startsWith("agent_") || r.kind === "estimated") model += r.amount_minor;
      else if (r.kind === "human_time") supervision += r.amount_minor;
      else if (r.visibility === "agency") other += r.amount_minor;
    }
    const cost = model + supervision + other;
    return {
      currency,
      charged_minor: charged,
      model_minor: model,
      supervision_minor: supervision,
      other_minor: other,
      cost_minor: cost,
      margin_minor: charged - cost,
    };
  }

  const add = (a: Money, b: Money): Money => ({
    currency: a.currency,
    charged_minor: a.charged_minor + b.charged_minor,
    model_minor: a.model_minor + b.model_minor,
    supervision_minor: a.supervision_minor + b.supervision_minor,
    other_minor: a.other_minor + b.other_minor,
    cost_minor: a.cost_minor + b.cost_minor,
    margin_minor: a.margin_minor + b.margin_minor,
  });
  const zero = (currency: string): Money => ({
    currency,
    charged_minor: 0,
    model_minor: 0,
    supervision_minor: 0,
    other_minor: 0,
    cost_minor: 0,
    margin_minor: 0,
  });

  function costToServe(me: MeResponse, url: URL): Response {
    const month = url.searchParams.get("month");
    if (month !== null && !MONTH.test(month)) return validation("month must be a month key");
    const des: CostToServe["des"] = [];
    const clients: CostToServe["clients"] = [];
    const templates = new Map<
      string,
      { row: CostToServe["templates"][number]; cos: Set<string> }
    >();
    for (const c of operated(me)) {
      const m = month ?? nowIso().slice(0, 7);
      const currency = c.reporting_currency;
      let total = zero(currency);
      let count = 0;
      for (const de of deps.desOf(c.id)) {
        const rows = ledgerOf(de).filter(
          (r) => r.currency === currency && r.occurred_at.slice(0, 7) === m,
        );
        if (!rows.length) continue;
        const money = split(rows, currency);
        des.push({
          company: refOf(c),
          month: m,
          de: { membership_id: de.membership_id, display_name: de.display_name },
          template_key: de.template_key,
          seat: { id: de.seat_id, title: de.seat_title },
          supervision_minutes: humanMinutesInMonth(de, m),
          ...money,
        });
        total = add(total, money);
        count += 1;
        const key = `${de.template_key}|${currency}`;
        const t = templates.get(key) ?? {
          row: { template_key: de.template_key, de_count: 0, companies: 0, ...zero(currency) },
          cos: new Set<string>(),
        };
        t.row = { ...t.row, ...add(t.row, money), de_count: t.row.de_count + 1 };
        t.cos.add(c.id);
        t.row.companies = t.cos.size;
        templates.set(key, t);
      }
      if (count) clients.push({ company: refOf(c), month: m, de_count: count, ...total });
    }
    const body: CostToServe = {
      complete: true,
      incomplete_reasons: [],
      des,
      clients,
      templates: [...templates.values()]
        .map((t) => t.row)
        .sort(
          (a, b) =>
            a.template_key.localeCompare(b.template_key) || a.currency.localeCompare(b.currency),
        ),
    };
    return json(body);
  }

  // Break-glass -------------------------------------------------------------------------------

  /** The server's expiry job: an active session past its end reads `expired`. */
  function settle(s: BreakGlassSession): BreakGlassSession {
    if (s.state === "active" && s.expires_at && s.expires_at <= nowIso()) {
      s.state = "expired";
      s.ended_at = s.expires_at;
      s.updated_at = s.expires_at;
    }
    return s;
  }
  const all = () => state.sessions.map(settle);
  const newestFirst = (a: BreakGlassSession, b: BreakGlassSession) =>
    b.requested_at.localeCompare(a.requested_at) || b.id.localeCompare(a.id);

  let seq = 0x100;
  const forbidden = (detail: string) => problem(403, "forbidden", "Forbidden", detail);
  const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
  const stale = (s: BreakGlassSession) =>
    deps.now().getTime() - new Date(s.requested_at).getTime() > REQUEST_TTL_MS;

  function stateFilter(url: URL): string | null | Response {
    const s = url.searchParams.get("state");
    if (s !== null && !["requested", "active", "rejected", "ended", "expired"].includes(s))
      return validation("state is not a break-glass state");
    return s;
  }

  /** `{ reason }` bodies: required on reject, optional on end. */
  function reasonOf(body: unknown, required: boolean): string | null | Response {
    const b = (body ?? {}) as Record<string, unknown>;
    if (typeof b !== "object" || Array.isArray(b)) return validation("body must be an object");
    if (Object.keys(b).some((k) => k !== "reason")) return validation("unknown body field");
    if (b.reason === undefined) return required ? validation("reason is required") : null;
    if (typeof b.reason !== "string") return validation("reason must be a string");
    const r = b.reason.trim();
    if (r.length < 1 || r.length > 2000) return validation("reason must be 1 to 2000 characters");
    return r;
  }

  const decide = (
    s: BreakGlassSession,
    me: MeResponse,
    kind: "agency_admin" | "company_owner",
    rejectReason: string | null,
  ) => {
    const at = nowIso();
    Object.assign(s, {
      decided_by_kind: kind,
      decided_by_user_id: me.user.id,
      decided_by_name: me.user.name,
      decided_at: at,
      updated_at: at,
      ...(rejectReason === null
        ? {
            state: "active",
            starts_at: at,
            // From `at` itself, so a clock tick between the two reads never stretches the span.
            expires_at: new Date(new Date(at).getTime() + s.duration_minutes * MIN).toISOString(),
          }
        : { state: "rejected", reject_reason: rejectReason, ended_at: at }),
    });
    return s;
  };

  const end = (s: BreakGlassSession, me: MeResponse, reason: string | null) => {
    const at = nowIso();
    Object.assign(s, {
      state: "ended",
      ended_at: at,
      ended_by_user_id: me.user.id,
      ended_by_name: me.user.name,
      end_reason: reason,
      updated_at: at,
    });
    return s;
  };

  /** A session of the caller's agency on a company it operates, else null (404). */
  function operatorSession(me: MeResponse, id: string | undefined): BreakGlassSession | null {
    const agency = me.agency;
    if (!agency) return null;
    const ids = new Set(operated(me).map((c) => c.id));
    const s = all().find(
      (x) => x.id === id && x.agency_id === agency.agency_id && ids.has(x.company_id),
    );
    return s ?? null;
  }

  function request(me: MeResponse, body: unknown): Response {
    const agency = me.agency;
    if (!agency) return notFound();
    const b = (body ?? {}) as Record<string, unknown>;
    if (typeof b !== "object" || Array.isArray(b)) return validation("body must be an object");
    const allowed = ["company_id", "reason", "duration_minutes", "scope", "scope_id"];
    if (Object.keys(b).some((k) => !allowed.includes(k))) return validation("unknown body field");
    if (typeof b.company_id !== "string" || !UUID.test(b.company_id))
      return validation("company_id must be a uuid");
    const reason = typeof b.reason === "string" ? b.reason.trim() : "";
    if (reason.length < 10 || reason.length > 2000)
      return validation("reason must be 10 to 2000 characters");
    const duration = b.duration_minutes;
    if (
      typeof duration !== "number" ||
      !Number.isInteger(duration) ||
      duration < 5 ||
      duration > 240
    )
      return validation("duration_minutes must be a whole number from 5 to 240");
    if (typeof b.scope !== "string" || !(SCOPES as readonly string[]).includes(b.scope))
      return validation("scope must be company, space or channel");
    const scopeId = b.scope_id ?? null;
    if (scopeId !== null && (typeof scopeId !== "string" || !UUID.test(scopeId)))
      return validation("scope_id must be a uuid");
    if ((b.scope === "company") !== (scopeId === null))
      return validation(
        "scope_id: a space or channel scope names its id; a company scope names none",
      );
    const company = operated(me).find((c) => c.id === b.company_id);
    if (!company) return notFound();
    const live = all().some(
      (s) =>
        s.company_id === company.id &&
        s.requested_by_user_id === me.user.id &&
        (s.state === "requested" || s.state === "active"),
    );
    if (live) return conflict("You already have a live break-glass session on this company.");
    const at = nowIso();
    const s: BreakGlassSession = {
      id: uid(0xb6a0, ++seq),
      agency_id: agency.agency_id,
      company_id: company.id,
      requested_by_user_id: me.user.id,
      requested_by_name: me.user.name,
      reason,
      scope: b.scope as BreakGlassSession["scope"],
      scope_id: scopeId as string | null,
      duration_minutes: duration,
      state: "requested",
      requested_at: at,
      decided_by_kind: null,
      decided_by_user_id: null,
      decided_by_name: null,
      decided_at: null,
      reject_reason: null,
      starts_at: null,
      expires_at: null,
      ended_at: null,
      ended_by_user_id: null,
      ended_by_name: null,
      end_reason: null,
      created_at: at,
      updated_at: at,
    };
    state.sessions.push(s);
    return json(s, 201);
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "operator.approvalsSla.get": ({ me, url }) => (me.agency ? approvalsSla(me, url) : notFound()),
    "operator.supervisorLoad.get": ({ me, url }) =>
      me.agency ? supervisorLoad(me, url) : notFound(),
    "operator.costToServe.get": ({ me, url }) => (me.agency ? costToServe(me, url) : notFound()),

    "operator.breakGlass.list": ({ me, url }) => {
      const agency = me.agency;
      if (!agency) return notFound();
      const st = stateFilter(url);
      if (st instanceof Response) return st;
      const companyId = url.searchParams.get("company_id");
      const ids = new Set(operated(me).map((c) => c.id));
      const rows = all()
        .filter(
          (s) =>
            s.agency_id === agency.agency_id &&
            ids.has(s.company_id) &&
            (st === null || s.state === st) &&
            (companyId === null || s.company_id === companyId),
        )
        .sort(newestFirst);
      return json(page(rows, url));
    },
    "operator.breakGlass.request": ({ me, body }) => request(me, body),
    "operator.breakGlass.approve": ({ me, params }) => {
      const s = operatorSession(me, params.session_id);
      if (!s) return notFound();
      if (me.agency?.role !== "admin")
        return forbidden("Only an agency admin approves break-glass.");
      if (s.requested_by_user_id === me.user.id)
        return forbidden("A second admin approves: you made this request.");
      if (s.state !== "requested") return conflict(`The request is ${s.state}.`);
      if (stale(s)) return conflict("The request is older than 24 hours and has expired.");
      return json(decide(s, me, "agency_admin", null));
    },
    "operator.breakGlass.reject": ({ me, params, body }) => {
      const reason = reasonOf(body, true);
      if (reason instanceof Response) return reason;
      const s = operatorSession(me, params.session_id);
      if (!s) return notFound();
      if (me.agency?.role !== "admin")
        return forbidden("Only an agency admin rejects break-glass.");
      if (s.requested_by_user_id === me.user.id)
        return forbidden("A second admin decides: you made this request.");
      if (s.state !== "requested") return conflict(`The request is ${s.state}.`);
      if (stale(s)) return conflict("The request is older than 24 hours and has expired.");
      return json(decide(s, me, "agency_admin", reason));
    },
    "operator.breakGlass.end": ({ me, params, body }) => {
      const reason = reasonOf(body, false);
      if (reason instanceof Response) return reason;
      const s = operatorSession(me, params.session_id);
      if (!s) return notFound();
      if (s.requested_by_user_id !== me.user.id && me.agency?.role !== "admin")
        return forbidden("Only the requester or an agency admin ends a session.");
      if (s.state !== "active") return conflict(`The session is ${s.state}.`);
      return json(end(s, me, reason));
    },

    "breakGlass.list": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden("Owners and admins see break-glass sessions.");
      const st = stateFilter(url);
      if (st instanceof Response) return st;
      const rows = all()
        .filter((s) => s.company_id === principal.company.id && (st === null || s.state === st))
        .sort(newestFirst);
      return json(page(rows, url));
    },
    // BREAK-GLASS: the read log is the company's owners' and admins' (never the agency's).
    "breakGlass.reads": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!principal.roles.some((r) => r === "owner" || r === "admin"))
        return forbidden("The break-glass read log is the company's owners' and admins'.");
      const sessionId = url.searchParams.get("session_id");
      if (sessionId !== null && !UUID.test(sessionId))
        return validation("session_id must be a uuid");
      const rows = state.reads
        .filter(
          (r) =>
            r.company_id === principal.company.id &&
            (sessionId === null || r.session_id === sessionId),
        )
        .sort((a, b) => (a.read_at < b.read_at ? 1 : a.read_at > b.read_at ? -1 : 0));
      return json(page(rows, url));
    },
    "breakGlass.approve": ({ principal, params, me }) => {
      if (!principal) return notFound();
      if (!principal.roles.includes("owner"))
        return forbidden("Only the company's owner approves break-glass.");
      const s = all().find((x) => x.id === params.id && x.company_id === principal.company.id);
      if (!s) return notFound();
      if (s.state !== "requested") return conflict(`The request is ${s.state}.`);
      if (stale(s)) return conflict("The request is older than 24 hours and has expired.");
      return json(decide(s, me, "company_owner", null));
    },
    "breakGlass.reject": ({ principal, params, me, body }) => {
      const reason = reasonOf(body, true);
      if (reason instanceof Response) return reason;
      if (!principal) return notFound();
      if (!principal.roles.includes("owner"))
        return forbidden("Only the company's owner rejects break-glass.");
      const s = all().find((x) => x.id === params.id && x.company_id === principal.company.id);
      if (!s) return notFound();
      if (s.state !== "requested") return conflict(`The request is ${s.state}.`);
      if (stale(s)) return conflict("The request is older than 24 hours and has expired.");
      return json(decide(s, me, "company_owner", reason));
    },
    "breakGlass.end": ({ principal, params, me, body }) => {
      const reason = reasonOf(body, false);
      if (reason instanceof Response) return reason;
      if (!principal) return notFound();
      if (!principal.roles.some((r) => r === "owner" || r === "admin"))
        return forbidden("A company owner or admin ends a session here.");
      const s = all().find((x) => x.id === params.id && x.company_id === principal.company.id);
      if (!s) return notFound();
      if (s.state !== "active") return conflict(`The session is ${s.state}.`);
      return json(end(s, me, reason));
    },
  };

  return { handlers, facts };
}
